import React, { cloneElement } from 'react';
import { SearchIcon, FileTextIcon, BanknoteIcon, HeartHandshakeIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
export function NewHowItWorks() {
  const steps = [{
    number: '01',
    icon: <SearchIcon className="h-6 w-6" data-id="element-1440" />,
    title: 'Evaluamos tu propiedad',
    description: 'Analizamos la ubicación y valor comercial para determinar cuánto puedes recibir mensualmente.',
    color: 'from-[#2B5FD9] to-[#1E4BB8]',
    lightColor: 'bg-blue-50',
    darkColor: 'text-[#2B5FD9]'
  }, {
    number: '02',
    icon: <FileTextIcon className="h-6 w-6" data-id="element-1441" />,
    title: 'Definimos tu plan',
    description: 'Eliges el plazo (5 a 15 años) y si prefieres un pago inicial mayor o cuotas mensuales más altas.',
    color: 'from-purple-600 to-purple-500',
    lightColor: 'bg-purple-50',
    darkColor: 'text-purple-600'
  }, {
    number: '03',
    icon: <BanknoteIcon className="h-6 w-6" data-id="element-1442" />,
    title: 'Recibes tus pagos',
    description: 'Comienzas a recibir tu ingreso mensual en UF, depositado directamente a tu cuenta.',
    color: 'from-green-600 to-green-500',
    lightColor: 'bg-green-50',
    darkColor: 'text-green-600'
  }, {
    number: '04',
    icon: <HeartHandshakeIcon className="h-6 w-6" data-id="element-1443" />,
    title: 'Vives tranquilo',
    description: 'Sigues viviendo en tu casa con total normalidad, sin deudas y con mayor liquidez.',
    color: 'from-amber-600 to-amber-500',
    lightColor: 'bg-amber-50',
    darkColor: 'text-amber-600'
  }];
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="como-funciona" className="py-12 md:py-24 bg-gradient-to-b from-white via-[#F8FAFC] to-white relative overflow-hidden" data-id="element-1444">
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#2B5FD9]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-1445" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" data-id="element-1446" />

      <div className="container mx-auto max-w-7xl px-4 relative z-10" data-id="element-1447">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16" data-id="element-1448">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B5FD9]/10 rounded-full mb-3 md:mb-4" data-id="element-1449">
            <SparklesIcon className="h-4 w-4 text-[#2B5FD9]" data-id="element-1450" />
            <span className="text-sm font-semibold text-[#2B5FD9]" data-id="element-1451">
              Modelo simple
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4 text-[#0A1628]" data-id="element-1452">
            Así funciona el modelo de Creditual
          </h2>
          <p className="text-sm md:text-lg text-[#6B7280] max-w-2xl mx-auto px-4" data-id="element-1453">
            Convertimos tu patrimonio en flujo mensual
          </p>
        </div>

        {/* Desktop: Grid */}
        <div className="hidden md:grid md:grid-cols-4 gap-6 relative" data-id="element-1454">
          {/* Connecting line */}
          <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2B5FD9] via-purple-500 via-green-500 to-amber-500 opacity-20 z-0" data-id="element-1455" />

          {steps.map((step, index) => <div key={index} className="relative z-10" data-id="element-1456">
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col items-center text-center hover:-translate-y-1" data-id="element-1457">
                {/* Number */}
                <div className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-gray-50 mb-4" data-id="element-1458">
                  <span className={`text-sm font-bold bg-gradient-to-br ${step.color} bg-clip-text text-transparent`} data-id="element-1459">
                    {step.number}
                  </span>
                </div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} p-0.5 shadow-lg mb-6`} data-id="element-1460">
                  <div className={`w-full h-full ${step.lightColor} rounded-2xl flex items-center justify-center ${step.darkColor}`} data-id="element-1461">
                    {step.icon}
                  </div>
                </div>

                <h3 className="text-lg font-bold text-[#0A1628] mb-3" data-id="element-1462">
                  {step.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1463">
                  {step.description}
                </p>
              </div>
            </div>)}
        </div>

        {/* Mobile: Stack */}
        <div className="md:hidden space-y-4" data-id="element-1464">
          {steps.map((step, index) => <div key={index} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-start gap-4" data-id="element-1465">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} p-0.5 shadow-md`} data-id="element-1466">
                <div className={`w-full h-full ${step.lightColor} rounded-xl flex items-center justify-center ${step.darkColor}`} data-id="element-1467">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'h-5 w-5'
              })}
                </div>
              </div>
              <div data-id="element-1468">
                <div className="flex items-center gap-2 mb-1" data-id="element-1469">
                  <span className="text-xs font-bold text-gray-400" data-id="element-1470">
                    {step.number}
                  </span>
                  <h3 className="text-base font-bold text-[#0A1628]" data-id="element-1471">
                    {step.title}
                  </h3>
                </div>
                <p className="text-sm text-[#6B7280] leading-snug" data-id="element-1472">
                  {step.description}
                </p>
              </div>
            </div>)}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 md:mt-16" data-id="element-1473">
          <button onClick={() => scrollToSection('simulador')} className="inline-flex items-center gap-2 px-8 py-4 bg-[#2B5FD9] text-white text-lg font-semibold rounded-xl hover:bg-[#1E4BB8] hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-1474">
            <span data-id="element-1475">Simular mis ingresos</span>
            <ArrowRightIcon className="h-5 w-5" data-id="element-1476" />
          </button>
        </div>
      </div>
    </section>;
}